import { useState, useEffect, useRef, useCallback } from 'react';
import { IconButton, Badge, Tooltip, keyframes } from '@mui/material';
import { WifiSlash } from '@phosphor-icons/react';
import { BellRinging, Bell } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import useAuthStore from '@stores/authStore';
import { isTokenValid } from '@lib/auth';
import useSSE from '../../hooks/useSSE';
import { useActiveServiceRequests } from '../../hooks/useServiceRequest';
import { useNotificationCounts } from '../../hooks/useNotificationCounts';

const BELL_SILENCE_MS = 120000; // 2 minutes silence across tabs

// ── Bell swing animation ────────────────────────────────────────────────────
const swing = keyframes`
  0%   { transform: rotate(0deg); }
  10%  { transform: rotate(14deg); }
  20%  { transform: rotate(-12deg); }
  30%  { transform: rotate(10deg); }
  40%  { transform: rotate(-8deg); }
  50%  { transform: rotate(6deg); }
  60%  { transform: rotate(-4deg); }
  70%  { transform: rotate(2deg); }
  80%  { transform: rotate(-1deg); }
  90%  { transform: rotate(0.5deg); }
  100% { transform: rotate(0deg); }
`;

// ── Notification chime (HTMLAudioElement) ───────────────────────────────────
// Mobile browsers block audio until a user gesture "unlocks" it.  We pre-load
// an Audio element and silently play it on the first tap/click so that future
// programmatic plays (e.g. when an SSE arrives) work without a gesture.

let bellAudio = null;
let audioUnlocked = false;

function getBellAudio() {
  if (!bellAudio) {
    bellAudio = new Audio('/ding.mp3');
    bellAudio.preload = 'auto';
  }
  return bellAudio;
}

function unlockAudio() {
  if (audioUnlocked) return;
  const audio = getBellAudio();
  // Silent play to satisfy browser autoplay policy
  audio.volume = 0;
  audio.play().then(() => {
    audio.pause();
    audio.currentTime = 0;
    audio.volume = 1;
    audioUnlocked = true;
  }).catch(() => {});
}

function playServiceBellChime() {
  try {
    const audio = getBellAudio();
    audio.currentTime = 0;
    audio.volume = 1;
    audio.play().catch(() => {});
  } catch {
    // Audio not available
  }
}

// ── Component ───────────────────────────────────────────────────────────────
export default function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = useAuthStore((s) => s.token);
  const restaurantId = useAuthStore((s) => s.restaurantId);
  const silenceKey = `dineo:serviceBell:silenceUntil:${restaurantId || 'global'}`;

  // Only activate when the token is present and not expired
  const isReady = !!token && isTokenValid(token) && !!restaurantId;

  const [ringing, setRinging] = useState(false);
  const [silencedUntil, setSilencedUntil] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const raw = window.localStorage.getItem(silenceKey);
    const parsed = Number.parseInt(raw || '0', 10);
    return Number.isFinite(parsed) ? parsed : 0;
  });
  const intervalRef = useRef(null);
  const prevPendingRef = useRef(0);
  const isSilenced = Date.now() < silencedUntil;

  // Unlock audio only while the staff notification bell is actually active.
  // Public pages such as /discover must never register service-bell audio hooks.
  useEffect(() => {
    if (!isReady || typeof window === 'undefined') return undefined;
    const events = ['click', 'touchstart', 'keydown'];
    const handler = () => {
      unlockAudio();
      events.forEach((eventName) => window.removeEventListener(eventName, handler, true));
    };
    events.forEach((eventName) =>
      window.addEventListener(eventName, handler, { capture: true, once: true }),
    );
    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, handler, true));
    };
  }, [isReady]);

  const applyBellSilence = useCallback(() => {
    const until = Date.now() + BELL_SILENCE_MS;
    setSilencedUntil(until);
    try {
      window.localStorage.setItem(silenceKey, String(until));
    } catch {
      // ignore localStorage errors
    }
  }, [silenceKey]);

  // Reload silence state when workspace/restaurant changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const raw = window.localStorage.getItem(silenceKey);
    const parsed = Number.parseInt(raw || '0', 10);
    setSilencedUntil(Number.isFinite(parsed) ? parsed : 0);
  }, [silenceKey]);

  // Keep silence state in sync across browser tabs
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const onStorage = (event) => {
      if (event.key !== silenceKey) return;
      const parsed = Number.parseInt(event.newValue || '0', 10);
      const next = Number.isFinite(parsed) ? parsed : 0;
      setSilencedUntil(next);
      if (Date.now() < next) {
        setRinging(false);
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [silenceKey]);

  // Auto-clear ringing while silence window is active
  useEffect(() => {
    if (isSilenced) {
      setRinging(false);
    }
  }, [isSilenced]);

  // Pending (unacknowledged) requests count
  const { data } = useActiveServiceRequests(isReady ? restaurantId : null);
  const pendingCount = data?.pending?.length ?? 0;

  // Total notification count (orders + service requests)
  const counts = useNotificationCounts();
  const totalCount = counts.total ?? 0;

  // Start ringing when new pending requests appear, stop when all confirmed
  useEffect(() => {
    if (pendingCount > prevPendingRef.current && !isSilenced) {
      setRinging(true);
    } else if (pendingCount === 0 && ringing) {
      setRinging(false);
    }
    prevPendingRef.current = pendingCount;
  }, [pendingCount, ringing, isSilenced]);

  // SSE: listen for new service requests (staff endpoint — requires auth)
  const handleSSEMessage = useCallback(
    (msg) => {
      queryClient.invalidateQueries({ queryKey: ['serviceRequests'] });
      if (msg?.request?.status === 'PENDING' && !isSilenced) {
        setRinging(true);
      }
    },
    [queryClient, isSilenced],
  );

  const { isConnected: sseConnected } = useSSE(`/service-requests/restaurant/${restaurantId}/stream`, {
    onMessage: handleSSEMessage,
    enabled: isReady,
  });

  // Repeating chime while ringing
  useEffect(() => {
    if (ringing && !isSilenced) {
      playServiceBellChime();
      intervalRef.current = setInterval(playServiceBellChime, 4000);
    } else {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => clearInterval(intervalRef.current);
  }, [ringing, isSilenced]);

  // Click: stop ringing + navigate to service-requests
  const handleClick = useCallback(() => {
    applyBellSilence();
    setRinging(false);
    navigate('/service-requests');
  }, [navigate, applyBellSilence]);

  // Don't render anything without valid auth
  if (!isReady) return null;

  const BellIcon = ringing ? BellRinging : !sseConnected ? WifiSlash : Bell;
  const bellTooltip = !sseConnected
    ? 'Live-Verbindung getrennt'
    : '';

  return (
    <Tooltip title={bellTooltip} arrow disableHoverListener={!bellTooltip}>
      <IconButton
        size="medium"
        aria-label="Service-Anfragen"
        onClick={handleClick}
        sx={{
          color: !sseConnected && restaurantId
            ? 'text.disabled'
            : ringing ? 'warning.main' : 'text.secondary',
          animation: ringing
            ? `${swing} 1s ease-in-out infinite`
            : 'none',
          transformOrigin: 'top center',
        }}
      >
        <Badge
          badgeContent={totalCount}
          color="error"
          invisible={totalCount === 0 && !ringing}
          max={99}
        >
          <BellIcon size={22} weight={ringing ? 'fill' : 'regular'} />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}
